<script setup>
import CallbackRequestActionsCell from "@frontJs/components/tables/cells/CallbackRequestActionsCell.vue";
import CallbackRequestDetailsCell from "@frontJs/components/tables/cells/CallbackRequestDetailsCell.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { toProcess, processed, callback } = defineProps({
  toProcess: {
    type: Boolean,
    required: false,
  },
  processed: {
    type: Boolean,
    required: false,
  },
  callback: {
    type: Function,
    required: false,
  },
});

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "tenants.name",
      header: () => t("label.customer"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.tenant.name,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "firstname",
      header: () => t("label.firstname"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.firstname,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "lastname",
      header: () => t("label.lastname"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.lastname,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "email",
      header: () => t("label.email"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.email,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "phone",
      header: () => t("label.phone"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.phone,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "subject",
      header: () => t("label.subject"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.subject,
        }),
    }),
    columnHelper.display({
      id: "details",
      header: t("label.details"),
      cell: (innerProps) =>
        h(CallbackRequestDetailsCell, {
          row: innerProps.row,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "processedAt",
      header: () => t("label.processedAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.processedAt,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "createdAt",
      header: () => t("label.createdAt"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.createdAt,
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (props) =>
        h(CallbackRequestActionsCell, {
          row: props.row,
          callback: () => {
            if (callback) {
              callback();
            }

            updateData();
          },
        }),
    }),
  ],
  url: route("front.postCallbackRequests"),
  data: {
    toProcess,
    processed,
  },
  columnVisibility: {
    processedAt: processed,
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <DataTableLayout
    v-else
    :table="table"
    :count="count"
    :global-filter="globalFilter"
    :set-global-filter="setGlobalFilter"
    :available-filters="availableFilters"
    :filters="filters"
    :set-filters="setFilters"
  />
</template>
